import React, {useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Grid,
    Modal,
    ModalOverlay,
    ModalContent,
    Image,
    Text,
    Flex,
    useMultiStyleConfig,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage} from 'react-intl'
import CloudinaryImage from '../../cloudinary-media/image'
import Video from './video'
import {getUrl} from '../../../utils/cloudinary-utils'
import useTagManager from '../../../hooks/use-tag-manager'
import {scheduleAfterPaint, scheduleIdleAnalytics} from '../../../utils/common-utils'

const DesktopGallery = ({images, desktopGalleryEngImages, productName}) => {
    // Safely inject the Engraved images in the Desktop Gallery.
    // 1. Remove already existing engraved images
    let cleanedImages = images.filter((img) => !img.isEngravedImg)

    // 2. Collect fresh engraved images
    const engravedImages = []
    if (desktopGalleryEngImages) {
        Object.values(desktopGalleryEngImages).forEach((face) => {
            if (face?.image) {
                const engravedTitle = `Engraved ${face.name} Side`

                if (!engravedImages.some((img) => img.title === engravedTitle)) {
                    engravedImages.push({
                        alt: engravedTitle,
                        title: engravedTitle,
                        isEngravedImg: true,
                        disBaseLink: face.image,
                        link: face.image
                    })
                }
            }
        })
    }

    // 3. Inject engraved images based on condition
    if (
        engravedImages?.length === 1 &&
        engravedImages[0].title &&
        engravedImages[0].title?.includes('Back')
    ) {
        // Insert after 1st image (if at least one image exists)
        if (cleanedImages?.length > 0) {
            cleanedImages = [cleanedImages[0], engravedImages[0], ...cleanedImages.slice(1)]
        } else {
            // no normal images → just return the engraved one
            cleanedImages = [engravedImages[0]]
        }
    } else {
        // Prepend engraved images
        cleanedImages = [...engravedImages, ...cleanedImages]
    }

    const styles = useMultiStyleConfig('ImageGallery')
    const tagManager = useTagManager()
    const [zoomIndex, setZoomIndex] = useState(null)
    const [isZoomedIn, setIsZoomedIn] = useState(false)
    const [isDragging, setIsDragging] = useState(false)
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const [position, setPosition] = useState({x: 0, y: 0})
    const [start, setStart] = useState({x: 0, y: 0})

    const imageRef = useRef(null)

    const openZoom = (index) => {
        // Mounting the full-screen, high-resolution gallery is a large render. Let the
        // click paint first, then open it without making analytics part of the interaction.
        scheduleAfterPaint(() => {
            setZoomIndex(index)
            setPosition({x: 0, y: 0})
        })
        scheduleIdleAnalytics(() => tagManager.thumbnailClick(index + 1, productName))
    }

    const closeZoom = () => {
        setZoomIndex(null)
        setIsZoomedIn(false)
        setPosition({x: 0, y: 0})
    }

    const handleMouseDown = (e) => {
        setIsDragging(true)
        setStart({x: e.clientX - position.x, y: e.clientY - position.y})
    }

    const handleMouseMove = (e) => {
        if (!isZoomedIn) {
            return
        }

        if (isDragging) {
            let x = e.clientX - start.x
            const threshold = isZoomedIn ? 350 : 450
            let y = e.clientY - start.y
            if (x > threshold) {
                x = threshold
            } else if (x < -threshold) {
                x = -threshold
            }
            if (y > threshold) {
                y = threshold
            } else if (y < -threshold) {
                y = -threshold
            }
            setPosition({x, y})
        }
    }

    const handleMouseUp = () => {
        setIsDragging(false)
    }

    return (
        <>
            <Grid
                direction="column"
                templateColumns="repeat(2, 1fr)"
                className="desktop-gallery-wrapper"
            >
                {cleanedImages?.map((image, index) => {
                    // 1-image PDPs match Figma giftcard layout (full gallery width).
                    // Odd counts (3, 5, …) keep the last tile half-width — do not stretch.
                    const isWideImage = cleanedImages.length === 1
                    const isVideo =
                        image.disBaseLink.includes('video') || image.disBaseLink.includes('mp4')

                    return (
                        <Box
                            key={index}
                            className="img-wrapper"
                            position="relative"
                            gridColumn={{
                                base: 'span 2',
                                xl: isWideImage ? 'span 2' : 'auto'
                            }}
                            cursor="zoom-in"
                            onClick={() => {
                                if (!isVideo) {
                                    openZoom(index)
                                } else {
                                    scheduleIdleAnalytics(() =>
                                        tagManager.videoClick(index + 1, productName)
                                    )
                                }
                            }}
                        >
                            {isVideo ? (
                                <Video
                                    src={image.disBaseLink}
                                    isModalOpen={isDesktop && zoomIndex !== null}
                                    videoAlt={image.alt}
                                    videoEnableSound={true}
                                    videoDisableAutoPlay={false}
                                    deferUntilVisible={index >= 2}
                                    aspectRatio="469:587"
                                />
                            ) : image?.isEngravedImg ? (
                                <Image
                                    src={image.disBaseLink}
                                    alt={image.alt}
                                    mixBlendMode="multiply"
                                    loading={index < 2 ? 'eager' : 'lazy'}
                                    fetchPriority={index < 2 ? 'high' : 'auto'}
                                />
                            ) : (
                                <CloudinaryImage
                                    src={image.disBaseLink}
                                    alt={image.alt}
                                    lazyload={index >= 2}
                                    width={isWideImage ? 450 : 225}
                                    aspectRatio="469:587"
                                    imgSizes={{
                                        xl: isWideImage
                                            ? 'calc((100vw - 500px))'
                                            : 'calc((100vw - 500px) / 2)',
                                        lg: 'calc((100vw - 500px))',
                                        base: '100vw'
                                    }}
                                    sizes={
                                        isWideImage
                                            ? [450, 650, 768, 1152, 1290, 1536, 1920, 2500, 3500]
                                            : [225, 325, 384, 576, 645, 768, 960, 1250, 1750]
                                    }
                                    styles={{
                                        aspectRatio: '469/587',
                                        width: '100%',
                                        mixBlendMode: 'multiply',
                                        height: 'auto',
                                        htmlWidth: 469,
                                        htmlHeight: 587
                                    }}
                                />
                            )}
                        </Box>
                    )
                })}
            </Grid>

            <Modal
                isOpen={isDesktop && zoomIndex !== null}
                onClose={closeZoom}
                size="full"
                allowPinchZoom={true}
                isCentered
                motionPreset="none"
            >
                <ModalOverlay bg="gray.100" />
                <ModalContent {...styles.zoomModel.content}>
                    <Flex {...styles.zoomModel.mainWrapper}>
                        <Box onClick={closeZoom} {...styles.zoomModel.exitZoom.main}>
                            <Image
                                alt="Close Zoom"
                                src={getUrl('UIImages/images/close-zoom.svg')}
                            />
                            <Text {...styles.zoomModel.exitZoom.text}>
                                <FormattedMessage id="exit.zoom" defaultMessage="Exit Zoom" />
                            </Text>
                        </Box>
                        <Box {...styles.zoomModel.leftSection.main}>
                            <Box gap="15px" display="inline-flex">
                                <Image
                                    alt="Image Drag"
                                    src={getUrl('UIImages/images/pdp-image-drag.svg')}
                                />
                                <Text {...styles.zoomModel.leftSection.text}>
                                    <FormattedMessage
                                        id="double.click.to.zoom"
                                        defaultMessage="Double-click to zoom"
                                    />
                                </Text>
                            </Box>
                            <Box gap="15px" display="inline-flex">
                                <Image
                                    alt="Image Zoom"
                                    src={getUrl('UIImages/images/pdp-image-zoom.svg')}
                                />
                                <Text {...styles.zoomModel.leftSection.text}>
                                    <FormattedMessage
                                        id="hold.drag"
                                        defaultMessage="Hold & drag to pan"
                                    />
                                </Text>
                            </Box>
                        </Box>

                        <Box
                            height="100%"
                            width="100%"
                            overflow="hidden"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            onDoubleClick={(e) => {
                                const isImage = e.currentTarget?.querySelector('.product-image')
                                if (!isZoomedIn && isImage) {
                                    scheduleIdleAnalytics(() =>
                                        tagManager.pdpCtaClick(
                                            'cta click - image zoom',
                                            productName
                                        )
                                    )
                                }
                                setIsZoomedIn(!isZoomedIn)
                                setPosition({x: 0, y: 0})
                            }}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            cursor={
                                cleanedImages[zoomIndex]?.disBaseLink.includes('video') ||
                                cleanedImages[zoomIndex]?.disBaseLink.includes('mp4')
                                    ? 'auto'
                                    : isZoomedIn
                                    ? 'zoom-out'
                                    : 'zoom-in'
                            }
                            sx={{
                                video: {
                                    width: '476.19px !important',
                                    margin: 'auto !important',
                                    objectFit: 'contain !important'
                                },
                                button: {
                                    display: 'none'
                                }
                            }}
                        >
                            {!(
                                cleanedImages[zoomIndex]?.disBaseLink.includes('video') ||
                                cleanedImages[zoomIndex]?.disBaseLink.includes('mp4')
                            ) ? (
                                <Image
                                    ref={imageRef}
                                    className="product-image"
                                    src={cleanedImages[zoomIndex]?.disBaseLink?.replace(
                                        /\/w_\d+/,
                                        '/w_1600'
                                    )}
                                    alt={cleanedImages[zoomIndex]?.alt}
                                    transform={`scale(${isZoomedIn ? 3 : 1}) translate(${
                                        position.x
                                    }px, ${position.y}px)`}
                                    transition="transform 0.2s ease"
                                    objectFit="contain"
                                    width="100%"
                                    height="100%"
                                    draggable={false}
                                    mixBlendMode="multiply"
                                />
                            ) : (
                                <Video
                                    src={cleanedImages[zoomIndex]?.disBaseLink}
                                    videoAlt={cleanedImages[zoomIndex]?.alt}
                                    isModalOpen={isDesktop && zoomIndex !== null}
                                    videoEnableSound={true}
                                    videoDisableAutoPlay={false}
                                    aspectRatio="469:587"
                                />
                            )}
                        </Box>

                        <Flex gap="8.77px" wrap="wrap" justify="center" mt={2}>
                            <Box {...styles.zoomModel.imageView}>
                                <Text fontSize="13px" fontWeight="400" letterSpacing="0.065px">
                                    {zoomIndex + 1} / {cleanedImages.length}
                                </Text>
                            </Box>
                            {cleanedImages.map((img, i) => (
                                <Box
                                    key={i}
                                    borderColor={
                                        i === zoomIndex ? 'black !important' : 'white !important'
                                    }
                                    border="2px solid"
                                    cursor="pointer"
                                    sx={{
                                        video: {
                                            width: '83.28px !important',
                                            height: '104px !important'
                                        },
                                        button: {
                                            display: 'none'
                                        }
                                    }}
                                    onClick={() => {
                                        setZoomIndex(i)
                                        setPosition({x: 0, y: 0})
                                        setIsZoomedIn(false)
                                    }}
                                >
                                    {img.disBaseLink.includes('video') ||
                                    img.disBaseLink.includes('mp4') ? (
                                        <Video
                                            src={img.disBaseLink}
                                            videoAlt={img.alt}
                                            isModalOpen={isDesktop && zoomIndex !== null}
                                            videoEnableSound={false}
                                            videoDisableAutoPlay={false}
                                            aspectRatio="63.24:104"
                                        />
                                    ) : (
                                        <Image
                                            src={img.disBaseLink}
                                            alt={img.alt}
                                            width="83.28px"
                                            height="104px"
                                            objectFit="cover"
                                        />
                                    )}
                                </Box>
                            ))}
                        </Flex>
                    </Flex>
                </ModalContent>
            </Modal>
        </>
    )
}

DesktopGallery.propTypes = {
    images: PropTypes.arrayOf(
        PropTypes.shape({
            disBaseLink: PropTypes.string,
            alt: PropTypes.string
        })
    ),
    productName: PropTypes.string,
    desktopGalleryEngImages: PropTypes.any
}

export default DesktopGallery
